import { useCallback, useEffect, useState } from 'react';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import OnboardingWizard from './OnboardingWizard';

// Gates every route under AppLayout behind first-run setup: verify org
// details -> accept the software license agreement -> plan reveal -> starter
// data preseed. Wraps <AppLayout/> in App.js, inside ProtectedRoute, so it
// only ever runs for an already-authenticated user.
export default function OnboardingGate({ children }) {
  const { user } = useAuth();
  const [status, setStatus] = useState(null);
  const [checking, setChecking] = useState(true);

  const fetchStatus = useCallback(async () => {
    setChecking(true);
    try {
      const { data } = await api.get('/api/onboarding/status');
      setStatus(data);
    } catch {
      // Fail open: a broken status check must never permanently lock a
      // client out of their own CRM.
      setStatus({ onboarding_complete: true });
    } finally {
      setChecking(false);
    }
  }, []);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-sm text-muted-foreground font-mono">Loading...</div>
      </div>
    );
  }

  if (status?.onboarding_complete) return children;

  // Only a super_admin can attest to org details and sign the license on the
  // organization's behalf. Everyone else just has to wait for setup to finish.
  if (user?.role !== 'super_admin') {
    return (
      <div data-testid="onboarding-waiting-screen" className="min-h-screen flex items-center justify-center bg-background px-6">
        <div className="text-center max-w-sm">
          <h2 className="font-display text-xl font-bold text-foreground mb-2">Almost there</h2>
          <p className="text-sm text-muted-foreground">
            Your administrator is finishing account setup. Check back in a few minutes.
          </p>
        </div>
      </div>
    );
  }

  // Resume at whichever step matches what's actually been done -- a user who
  // accepted T&C but got interrupted before finishing preseed (or just
  // reloaded) should never have to re-confirm details or re-accept the
  // agreement, and should never land back on "org" once past it.
  let initialStep = 'org';
  if (status?.tc_accepted) initialStep = 'reveal';
  else if (status?.details_confirmed) initialStep = 'terms';

  return <OnboardingWizard initialStep={initialStep} onComplete={fetchStatus} />;
}
